import { useEffect, useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import { getErrorMessage } from '../../../lib/errors';
import { useToast } from '../../../components/ToastNB';
import { CardNB, ButtonNB, IconNB, InputNB, TimePickerNB, DaySelectorNB } from '../../../components/ui-nb';
import { error as logError } from '../../../lib/logger';

interface FormData {
  name: string;
  address: string;
  phone: string;
  scheduleStart: string;
  scheduleEnd: string;
  notes: string;
  operatingDays: string[];
}

export default function EditBranch() {
  const navigate = useNavigate();
  const { id, lang } = useParams();
  const locale = lang || 'en';
  const { addToast } = useToast();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    address: '',
    phone: '',
    scheduleStart: '',
    scheduleEnd: '',
    notes: '',
    operatingDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);

  useEffect(() => {
    if (!id) return;
    const loadBranch = async () => {
      try {
        const { data, error } = await supabase
          .from('branches')
          .select('*')
          .eq('id', id)
          .single();
        if (error) throw error;
        if (data) {
          setFormData({
            name: data.name || '',
            address: data.address || '',
            phone: data.phone || '',
            scheduleStart: data.schedule_start || '',
            scheduleEnd: data.schedule_end || '',
            notes: data.notes || '',
            operatingDays: data.operating_days || ['mon', 'tue', 'wed', 'thu', 'fri'],
          });
        }
      } catch (err) {
        logError('Error loading branch', { error: getErrorMessage(err) });
        const message = getErrorMessage(err) || 'Failed to load branch data.';
        addToast(message, 'error');
      } finally {
        setLoadingData(false);
      }
    };
    loadBranch();
  }, [id, addToast]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    const filteredValue = name === 'phone' ? value.replace(/[^\d\s\-\+]/g, '') : value;
    setFormData((prev) => ({ ...prev, [name]: filteredValue }));
    if (errors[name as keyof FormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('branches')
        .update({
          name: formData.name,
          address: formData.address,
          phone: formData.phone || null,
          schedule_start: formData.scheduleStart || null,
          schedule_end: formData.scheduleEnd || null,
          notes: formData.notes || null,
          operating_days: formData.operatingDays,
        })
        .eq('id', id);
      if (error) throw error;
      addToast('Branch updated successfully', 'success');
      navigate(`/${locale}/admin/branches/${id}`);
    } catch (err) {
      logError('Error updating branch', { error: getErrorMessage(err) });
      const message = getErrorMessage(err) || 'Error updating branch. Please try again.';
      addToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate(`/${locale}/admin/branches/${id}`);
  };

  if (loadingData) {
    return <p className="text-nb-text-muted">Loading branch data...</p>;
  }

  return (
    <>
      <div className="mb-6">
        <Link
          to={`/${locale}/admin/branches/${id}`}
          className="text-nb-black text-sm inline-flex items-center gap-1"
        >
          <IconNB name="chevronLeft" size={16} />
          Back to branch
        </Link>
      </div>

      <CardNB className="p-6 max-w-2xl">
        <h1 className="text-xl font-bold text-nb-black mb-6">Edit Branch</h1>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <InputNB
            label="Name *"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            error={errors.name}
            placeholder="e.g. Downtown Branch"
          />

          <InputNB
            label="Address *"
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            error={errors.address}
            placeholder="e.g. 500 Reform Ave, Downtown"
          />

          <InputNB
            label="Phone"
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="55 1234 5678"
          />

          <div>
            <label className="font-nb font-bold text-sm text-nb-black block mb-2">
              Schedule
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <TimePickerNB
                label="Opening Time"
                value={formData.scheduleStart}
                onChange={(value) => setFormData(prev => ({ ...prev, scheduleStart: value }))}
                placeholder="Select time"
              />
              <TimePickerNB
                label="Closing Time"
                value={formData.scheduleEnd}
                onChange={(value) => setFormData(prev => ({ ...prev, scheduleEnd: value }))}
                placeholder="Select time"
              />
            </div>
          </div>

          <DaySelectorNB
            label="Operating Days"
            value={formData.operatingDays}
            onChange={(days) => setFormData(prev => ({ ...prev, operatingDays: days }))}
          />

          <div>
            <label htmlFor="notes" className="font-nb font-bold text-sm text-nb-black block mb-1">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="bg-nb-white border-3 border-nb-black px-4 py-3 rounded-[2px] font-nb focus:border-nb-yellow focus:outline-none w-full resize-none"
              placeholder="Additional information about the branch"
            />
          </div>

          <div className="flex gap-4 pt-4">
            <ButtonNB type="submit" variant="primary" loading={loading}>
              {loading ? 'Saving...' : 'Update Branch'}
            </ButtonNB>
            <ButtonNB type="button" variant="secondary" onClick={handleCancel}>
              Cancel
            </ButtonNB>
          </div>
        </form>
      </CardNB>
    </>
  );
}